import { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { carrierName } from '@/lib/flights/api';
import { formatFlightDateTime } from '@/lib/disruptions/airport-tz';
import { isPastDay } from '@/lib/flights/search-day';
import { vendorLabel } from '@/lib/providers/integration-state';
import { useGroupFlightOffers } from '@/lib/rebooking/use-group-flight-offers';
import { FlightOffersSearchError } from '@/lib/rebooking/api';
import type {
  CabinClass,
  FlightProviderAttempt,
  GroupFlightOffer,
  GroupFlightOfferSegment,
  GroupFlightOffersView,
} from '@/lib/rebooking/types';

// Manual flight picker for ONE PNR group — the operator picks a flight a PROVIDER sells
// (contract `airline-air-ticketing.v1` §3): the tenant's flight providers are asked in
// order — the airline's own service, then Duffel, Atlas — for the group's party, and each
// offer shows who sells it and its seats — never the provider's fare: that is what Nexa
// pays, and the airline sees only prices (commercial-contract §6.13); an offer carries no
// price, so it shows no amount. Never the airline schedule: a schedule
// row has no seat behind it, and an option built from one is refused at ticket issue.
// The case supplies route, day, cabin and party; the operator may narrow origin /
// destination / day (and the host the cabin). The host applies the override with the
// picked offer's `offerRef`.

/** "H2 102" from a carrier-qualified ("H2102") or bare ("102") provider flight number. */
export function offerFlightCode(seg: Pick<GroupFlightOfferSegment, 'flightNumber' | 'marketingAirline'>): string {
  const n = seg.flightNumber.trim().toUpperCase();
  const carrier = seg.marketingAirline?.toUpperCase() ?? '';
  if (carrier && n.startsWith(carrier) && /^\d+$/.test(n.slice(carrier.length))) {
    return `${carrier} ${n.slice(carrier.length)}`;
  }
  return /^\d+$/.test(n) && carrier ? `${carrier} ${n}` : n;
}

/** The provider's name as Proveedores shows it (`vendorName.air-ticketing.*`). */
export function offerProviderName(t: TFunction<'network'>, provider: string): string {
  return vendorLabel(t, 'air-ticketing', provider);
}

/** Why one provider did or did not answer, in the operator's words. */
function attemptText(t: TFunction<'pnr'>, a: FlightProviderAttempt): string {
  switch (a.outcome) {
    case 'offers':
      return t('picker.attempt.offers', { count: a.offers ?? 0 });
    case 'empty':
      return t('picker.attempt.empty');
    case 'error':
      return t('picker.attempt.error');
    case 'skipped':
      return t('picker.attempt.skipped');
    default:
      return t(`picker.attempt.unavailable.${a.reason ?? 'fallback'}`, {
        defaultValue: t('picker.attempt.unavailable.fallback'),
      });
  }
}

function departureMs(o: GroupFlightOffer): number {
  const t = Date.parse(o.segments[0]?.departure ?? '');
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

export function FlightPicker({
  groupId,
  caseUrn,
  cabin,
  seats,
  defaultDate,
  defaultOrigin,
  defaultDestination,
  selectedOfferRef,
  onPick,
  onSearched,
}: {
  /** The PNR group (sub-case URN) the offers are for. */
  groupId: string;
  caseUrn?: string | undefined;
  /** The operator's cabin. Unset → the group's own cabin (what generation asks for). */
  cabin?: CabinClass | undefined;
  /** Party size — the domain prefers the group's own count. */
  seats?: number | undefined;
  /** The day to search first (the disrupted departure's, or today once that is past). */
  defaultDate?: string | undefined;
  /** The PNR's own itinerary, shown in the fields until the providers answer (and when the
   *  case has no route to search, so the operator only has to confirm it). */
  defaultOrigin?: string | undefined;
  defaultDestination?: string | undefined;
  selectedOfferRef?: string | undefined;
  onPick: (offer: GroupFlightOffer) => void;
  /** What the providers were asked — the host shows the cabin the domain resolved. */
  onSearched?: ((query: GroupFlightOffersView['query']) => void) | undefined;
}) {
  const { t } = useTranslation('pnr');
  const { t: tNetwork } = useTranslation('network');
  const [origin, setOrigin] = useState(defaultOrigin ?? '');
  const [destination, setDestination] = useState(defaultDestination ?? '');
  const [date, setDate] = useState(defaultDate ?? '');
  const [touched, setTouched] = useState(false);
  // Committed params drive the query. The first search leaves the route to the case (the
  // same target generation uses — a connection PNR's final destination via its hub); the
  // fields then show what the providers were asked, and Search re-asks with the edits.
  const [params, setParams] = useState<{
    origin?: string | undefined;
    destination?: string | undefined;
    date?: string | undefined;
  }>(
    defaultDate ? { date: defaultDate } : {},
  );
  const search = useGroupFlightOffers(groupId, caseUrn, { ...params, cabin, seats });
  const view = search.data;
  const offers = [...(view?.offers ?? [])].sort((a, b) => departureMs(a) - departureMs(b));

  useEffect(() => {
    if (!view || touched) return;
    setOrigin(view.query.origin);
    setDestination(view.query.destination);
    setDate(view.query.dateUtc);
  }, [view, touched]);

  useEffect(() => {
    if (view) onSearched?.(view.query);
  }, [view, onSearched]);

  // The case has no route the domain can search (422 ROUTE_REQUIRED): the operator gives
  // it — not «the providers are down».
  const routeRequired = search.error instanceof FlightOffersSearchError && search.error.code === 'ROUTE_REQUIRED';

  function runSearch(): void {
    setParams({
      origin: origin || undefined,
      destination: destination || undefined,
      date: date || undefined,
    });
  }

  function edit(set: (v: string) => void, value: string): void {
    setTouched(true);
    set(value);
  }

  const fieldCls =
    'h-8 rounded-md border border-input bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-primary/40';
  const attempts = view?.providerAttempts ?? [];

  return (
    <div className="space-y-2" data-testid="flight-picker">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <input
          className={fieldCls}
          value={origin}
          onChange={(e) => edit(setOrigin, e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          placeholder={t('picker.originPlaceholder')}
          aria-label={t('picker.origin')}
          maxLength={3}
        />
        <input
          className={fieldCls}
          value={destination}
          onChange={(e) => edit(setDestination, e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          placeholder={t('picker.destinationPlaceholder')}
          aria-label={t('picker.destination')}
          maxLength={3}
        />
        <input
          type="date"
          className={fieldCls}
          value={date}
          onChange={(e) => edit(setDate, e.target.value)}
          aria-label={t('picker.date')}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button size="sm" variant="outline" onClick={runSearch} data-testid="flight-picker-search">
          <Search className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('picker.search')}
        </Button>
        {view && offers.length > 0 ? (
          <span className="text-xs text-muted-foreground" data-testid="flight-picker-party">
            {t('picker.forParty', { count: view.query.seats })}
          </span>
        ) : null}
      </div>

      <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1" data-testid="flight-picker-results">
        {search.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {t('picker.searching')}
          </div>
        ) : routeRequired ? (
          <p className="py-3 text-sm text-amber-800 dark:text-amber-300" role="alert" data-testid="flight-picker-route-required">
            {t('picker.routeRequired')}
          </p>
        ) : search.isError ? (
          <p className="py-3 text-sm text-destructive" data-testid="flight-picker-error">{t('picker.error')}</p>
        ) : offers.length === 0 ? (
          <div className="space-y-1.5 py-3 text-sm text-muted-foreground" data-testid="flight-picker-empty">
            <p>
              {isPastDay(view?.query.dateUtc ?? params.date)
                ? t('picker.emptyPast')
                : view?.tooFewSeats
                  ? t('picker.tooFewSeats', { count: view.tooFewSeats, party: view.query.seats })
                  : attempts.length > 0
                    ? t('picker.noProviderOffers')
                    : view?.providerBacked
                      ? t('picker.noProviders')
                      : t('picker.empty')}
            </p>
            {attempts.length > 0 ? (
              <ol className="list-decimal space-y-0.5 pl-5 text-xs" data-testid="flight-picker-attempts">
                {attempts.map((a, i) => (
                  <li key={`${a.provider}-${i}`} data-testid="flight-picker-attempt">
                    <span className="font-medium text-foreground">{offerProviderName(tNetwork, a.provider)}</span>
                    {' — '}
                    {attemptText(t, a)}
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : (
          offers.map((o) => {
            const first = o.segments[0]!;
            const stops = o.segments.length - 1;
            const carrier = carrierName(first.marketingAirline);
            return (
              <button
                key={o.offerRef}
                type="button"
                onClick={() => onPick(o)}
                className={cn(
                  'w-full rounded-md border p-2 text-left transition-colors',
                  selectedOfferRef === o.offerRef
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/40'
                    : 'border-border hover:bg-muted',
                )}
                data-testid="flight-picker-option"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-baseline gap-2">
                    <span className="font-mono text-sm font-semibold">
                      {o.segments.map(offerFlightCode).join(' · ')}
                    </span>
                    {carrier ? <span className="text-xs text-muted-foreground">{carrier}</span> : null}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {formatFlightDateTime(first.departure, first.origin)}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {[first.origin, ...o.segments.map((s) => s.destination)].join(' → ')}
                  </span>
                  <span>{stops === 0 ? t('picker.nonstop') : t('picker.stops', { count: stops })}</span>
                  {o.provider ? (
                    <Badge variant="muted" size="sm" data-testid="flight-picker-provider">
                      {offerProviderName(tNetwork, o.provider)}
                    </Badge>
                  ) : null}
                  <span data-testid="flight-picker-seats">{t('picker.seats', { count: o.seatsAvailable })}</span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
